import { useEffect, useState } from "react";
import { BadgeCheck, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import TierPortal from "@/components/TierPortal";
import ThankButton from "@/components/ThankButton";
import TipButton from "@/components/TipButton";
import AuthorMenu from "@/components/AuthorMenu";
import UserLink from "@/components/UserLink";

/**
 * "Claimed by @username" card shown on claimed team / trainer / event detail pages.
 * Fetches the owner's public card and renders: a verified check (when verified), the
 * owner's thanks portal + avatar, the username LINKED to their public profile, and the
 * Thank / Tip / chevron-menu actions — matching the Event page attribution.
 */
export default function ClaimedByTag({ userId, contextType, contextId, reportType, verified = true }) {
    const [card, setCard] = useState(null);
    useEffect(() => {
        let active = true;
        if (!userId) { setCard(null); return undefined; }
        api.get(`/users/${userId}/card`)
            .then((r) => { if (active) setCard(r.data); })
            .catch(() => { if (active) setCard(null); });
        return () => { active = false; };
    }, [userId]);
    if (!card) return null;
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-4 w-full" data-testid="claimed-by-card">
            <div className="flex flex-col items-start gap-2 min-w-0">
                <span className="text-[9px] uppercase tracking-[0.1em] text-[#a0a0a0] shrink-0 flex items-center gap-1">
                    {verified && <BadgeCheck className="h-3.5 w-3.5 text-[#22c55e]" />}Claimed by
                </span>
                <div className="flex items-center gap-2 min-w-0 w-full">
                    <div className="shrink-0">
                        <TierPortal count={card.thanks_count} size={18} ring={1.5} gap={1} testid="claimed-by-portal">
                            {card.photo ? (
                                <img src={card.photo} alt={card.username || card.name} className="h-full w-full object-cover" />
                            ) : (
                                <span className="h-full w-full flex items-center justify-center bg-[#0f0f0f]">
                                    <UserIcon className="h-3 w-3 text-[#a0a0a0]" />
                                </span>
                            )}
                        </TierPortal>
                    </div>
                    <div className="min-w-0 truncate text-sm font-semibold text-white" data-testid="claimed-by-name">
                        <UserLink username={card.username} testid="claimed-by-username" />
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <ThankButton toUser={card.id} toName={card.username || card.name} contextType={contextType} contextId={contextId} initialCount={card.thanks_count} />
                        <TipButton tipLinks={card.tip_links} name={card.username || card.name} />
                        <AuthorMenu userId={card.id} name={card.username || card.name} reportType={reportType || null} reportId={reportType ? contextId : null} />
                    </div>
                </div>
            </div>
        </div>
    );
}
